/**
 * Token broadcast channel for cross-tab synchronization
 * Uses BroadcastChannel API to sync authentication state across browser tabs
 */

const CHANNEL_NAME = "pfz-cggenai-auth-channel";

export type TokenBroadcastMessage =
  | { type: "TOKEN_REFRESHED"; accessToken: string; expiresAt: number }
  | { type: "LOGOUT" };

type MessageHandler = (message: TokenBroadcastMessage) => void;

class TokenBroadcast {
  private channel: BroadcastChannel | null = null;
  private handlers: Set<MessageHandler> = new Set();
  private static instance: TokenBroadcast;

  private constructor() {
    this.initChannel();
  }

  static getInstance(): TokenBroadcast {
    if (!TokenBroadcast.instance) {
      TokenBroadcast.instance = new TokenBroadcast();
    }
    return TokenBroadcast.instance;
  }

  private initChannel(): void {
    // BroadcastChannel is not available in all environments (e.g., SSR, workers)
    if (typeof BroadcastChannel === "undefined") {
      console.warn("[TokenBroadcast] BroadcastChannel not available");
      return;
    }

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<TokenBroadcastMessage>) => {
        this.handlers.forEach((handler) => {
          try {
            handler(event.data);
          } catch (error) {
            console.error("[TokenBroadcast] Error in message handler:", error);
          }
        });
      };
    } catch (error) {
      console.error("[TokenBroadcast] Failed to create channel:", error);
    }
  }

  /**
   * Subscribe to token broadcast messages
   * @param handler Function to call when a message is received
   * @returns Cleanup function to unsubscribe
   */
  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Broadcast a token refresh to other tabs
   */
  broadcastTokenRefresh(accessToken: string, expiresAt: number): void {
    if (!this.channel) return;

    const message: TokenBroadcastMessage = {
      type: "TOKEN_REFRESHED",
      accessToken,
      expiresAt,
    };

    try {
      this.channel.postMessage(message);
    } catch (error) {
      console.error(
        "[TokenBroadcast] Failed to broadcast token refresh:",
        error,
      );
    }
  }

  /**
   * Broadcast a logout event to other tabs
   */
  broadcastLogout(): void {
    if (!this.channel) return;

    const message: TokenBroadcastMessage = { type: "LOGOUT" };

    try {
      this.channel.postMessage(message);
    } catch (error) {
      console.error("[TokenBroadcast] Failed to broadcast logout:", error);
    }
  }

  /**
   * Close the broadcast channel
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.handlers.clear();
  }
}

// Export singleton instance
export const tokenBroadcast = TokenBroadcast.getInstance();
